import {AfterViewInit, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {EmailTemplateModel, MergeTag} from '../model/offering-template.model';
import {UtilsService} from '@shared-services/utils.service';
import {SnackbarService} from '@ui-kit/framework/util/service/snackbar.service';
import {environment} from '@env/environment';
import CryptoJS from 'crypto-js';
import {ConsoleContextService} from "@security/console-context.service";
import {User} from "@ui-kit/framework/security/model/user.model";

declare var unlayer: any;


@Component({
  selector: 'app-email-template-designer',
  templateUrl: './email-template-designer.component.html',
  styleUrls: ['./email-template-designer.component.scss']
})
export class EmailTemplateDesignerComponent implements OnChanges, OnInit, AfterViewInit {
  @Input()
  selectedTemplate!: EmailTemplateModel | null;

  @Input()
  offeringUuid!: string;

  @Input()
  hideActionButtons: boolean = false;

  @Output()
  selectedTemplateChange: EventEmitter<EmailTemplateModel | null> = new EventEmitter<EmailTemplateModel | null>();

  @Output()
  missingRequiredMergeTags: EventEmitter<string[]> = new EventEmitter<string[]>();

  @Output()
  saveTemplate: EventEmitter<boolean> = new EventEmitter<boolean>();

  protected pageId: any;
  protected editorIsLoaded: boolean = false;
  protected readonly environment = environment;

  constructor(public utilsService: UtilsService,
              public contextService: ConsoleContextService,
              private snackbarService: SnackbarService) {
    // pageId is used as the id of the email component such that we can have multiple tabs of it
    this.pageId = utilsService.generateUuid();
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const userId = this.offeringUuid!;

      const signature = CryptoJS.HmacSHA256(userId, environment.unlayerUserSecret).toString();
      let user: User = this.contextService.currentUser!;
      unlayer.init({
        id: this.pageId,
        projectId: environment.unlayerApiProjectId,
        display: 'email',
        user: {
          id: userId,
          signature: signature,
          email: user.email,
          name: `${user.firstname} ${user.lastname}`
        },
        features: {
          undoRedo: true
        }
      });
      this.editorIsLoaded = true;

      if (this.selectedTemplate) {
        this.loadTemplate(this.selectedTemplate);
      }

      unlayer.addEventListener('design:updated', () => {
        this.validateMergeTagsLive();
      });

    }, 1000);
  }


  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedTemplate'] && !changes['selectedTemplate'].isFirstChange()) {
      this.selectedTemplate = changes['selectedTemplate'].currentValue;
      this.loadTemplate(this.selectedTemplate!);
    }
  }

  loadTemplate(template: EmailTemplateModel): void {
    if (template && this.editorIsLoaded) {
      if (template.mergeTags && template.mergeTags.length > 0) {
        const jsonData = this.parseMergeTagsIntoUnlyerJson(template);
        unlayer.setMergeTags(jsonData);
      } else {
        unlayer.setMergeTags({});
      }
      unlayer.loadDesign(template.design);
    }
  }

  // Need to reduce the merge tag array into one merge tag which contains all of the tags
  private parseMergeTagsIntoUnlyerJson(template: EmailTemplateModel): MergeTag {
    return template.mergeTags.reduce((acc, tag) => {
      const [fieldName, properties] = Object.entries(tag)[0];
      acc[fieldName] = properties;
      return acc;
    }, {});
  }
  clearTemplate() {
    if (this.editorIsLoaded) {
      unlayer.loadBlank();
    }
  }

  saveAndExportHtml(): void {
    unlayer.exportHtml((data: any) => {
      if (this.selectedTemplate) {
        // Step 1: Validate using the exported HTML
        const requiredTags = this.selectedTemplate.mergeTags.map(tag => Object.values(tag)[0].name);
        const usedTags = this.extractUsedMergeTagsFromHtml(data.html);
        const missingTags = requiredTags.filter(tag => !usedTags.has(tag));

        this.missingRequiredMergeTags.emit(missingTags);

        if (missingTags.length > 0) {
          this.snackbarService.openSnackBar('Please include all required dynamic values before saving.');
          this.saveTemplate.emit(false);
          return;
        }

        // Step 2: Update the selectedTemplate *after* passing validation
        this.selectedTemplate.design = data.design;
        this.selectedTemplate.html = data.html;

        this.selectedTemplateChange.emit(this.selectedTemplate);
        this.saveTemplate.emit(true);
      }
    }, {
      minify: true
    });
  }

  clearCurrentSelectedTemplate(): void {
    this.selectedTemplate = null;
    this.clearTemplate();
    this.selectedTemplateChange.emit(null);
  }

  private extractUsedMergeTagsFromHtml(html: string): Set<string> {
    const tagRegex = /{{(.*?)}}/g;
    const usedTags = new Set<string>();
    let match;
    while ((match = tagRegex.exec(html)) !== null) {
      usedTags.add(match[1].trim());
    }
    return usedTags;
  }

  private validateMergeTagsLive(): void {
    unlayer.exportHtml((data: any) => {
      if (this.selectedTemplate) {
        const requiredTags = this.selectedTemplate.mergeTags.map(tag => Object.values(tag)[0].name);
        const usedTags = this.extractUsedMergeTagsFromHtml(data.html);
        const missingTags = requiredTags.filter(tag => !usedTags.has(tag));
        this.missingRequiredMergeTags.emit(missingTags);
      }
    });
  }
}
